import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';

// Utility function to format timestamps in user's local timezone
const formatLocalDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    let date;
    
    // Handle timestamps without timezone (assume they are UTC)
    if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
      // For timestamps without timezone info, assume UTC and convert
      date = new Date(timestamp + 'Z');
    } else {
      date = new Date(timestamp);
    }
    
    // Ensure the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp);
      return 'Invalid Date';
    }
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Format Error';
  }
};

const OrderManagement = ({ apiClient, onPurchaseCapacity }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [tables, setTables] = useState([]);
  const [receiptConfig, setReceiptConfig] = useState({});
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [currency, setCurrency] = useState('USD'); // Restaurant currency
  const [restaurant, setRestaurant] = useState(null); // Restaurant data including capacity
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true); // Toggle for 120-second refresh
  const [lastUpdated, setLastUpdated] = useState(null); // Last updated timestamp
  // Payment modal removed as requested
  const [combineModal, setCombineModal] = useState({ show: false, selectedOrders: [] });
  const [showCombineConfirm, setShowCombineConfirm] = useState(false);
  const [combineConfirmData, setCombineConfirmData] = useState(null);

  const orderStatuses = [
    { value: '', label: 'All Orders' },
    { value: 'received', label: 'Received' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'ready', label: 'Ready' },
    { value: 'completed', label: 'Completed' }
  ];

  const paymentStatuses = [
    { value: '', label: 'All Payment Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' }
  ];

  useEffect(() => {
    fetchInitialData();
    
    // Set up simple 120-second auto-refresh if enabled
    let autoRefreshTimer = null;
    
    const setupAutoRefresh = () => {
      if (autoRefreshEnabled) {
        autoRefreshTimer = setInterval(() => {
          console.log('🔄 Auto-refresh orders (120s interval)');
          fetchOrders();
        }, 120000); // 120 seconds
        setRefreshInterval(autoRefreshTimer);
      }
    };
    
    setupAutoRefresh();
    
    return () => {
      if (autoRefreshTimer) clearInterval(autoRefreshTimer);
    };
  }, [autoRefreshEnabled]);

  useEffect(() => {
    fetchOrders();
  }, [selectedStatus, selectedPaymentStatus, selectedTable]);

  const fetchInitialData = async () => {
    try {
      const [ordersResponse, tablesResponse, receiptConfigResponse, profileResponse] = await Promise.all([
        apiClient.get('/restaurant/orders'),
        apiClient.get('/restaurant/tables'),
        apiClient.get('/restaurant/receipt-config').catch(() => ({ data: {} })), // Handle case where config doesn't exist yet
        apiClient.get('/restaurant/profile')
      ]);
      
      setOrders(ordersResponse.data);
      setTables(tablesResponse.data);
      setReceiptConfig(receiptConfigResponse.data);
      setRestaurant(profileResponse.data);
      setCurrency(profileResponse.data.currency || 'USD');
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load data');
      setCurrency('USD'); // Fallback currency
    }
  };

  const fetchOrders = async () => {
    try {
      const params = {};
      if (selectedStatus) params.order_status = selectedStatus;
      if (selectedPaymentStatus) params.payment_status = selectedPaymentStatus;
      if (selectedTable) params.table_number = selectedTable;
      
      const response = await apiClient.get('/restaurant/orders', { params });
      
      // Sort orders by latest received/modified (descending)
      const sortedOrders = response.data.sort((a, b) => {
        const aTime = Math.max(
          new Date(a.created_at).getTime(),
          new Date(a.updated_at || a.created_at).getTime()
        );
        const bTime = Math.max(
          new Date(b.created_at).getTime(),
          new Date(b.updated_at || b.created_at).getTime()
        );
        return bTime - aTime; // Descending order (latest first)
      });
      
      setOrders(sortedOrders);
      setLastUpdated(new Date()); // Update last updated timestamp
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    }
  };

  // Toggle auto-refresh on/off
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setLoading(true);
    try {
      const updateData = {
        order_status: newStatus
      };
      
      // When marking as completed, also set payment status to paid
      if (newStatus === 'completed') {
        updateData.payment_status = 'paid';
      }
      
      // When order is received and paid via cash, mark as paid  
      const currentOrder = orders.find(o => o.id === orderId);
      if (currentOrder && currentOrder.payment_method === 'cash' && currentOrder.payment_status !== 'paid') {
        updateData.payment_status = 'paid';
      }
      
      await apiClient.put(`/restaurant/orders/${orderId}/status`, updateData);
      toast.success('Order status updated successfully!');
      fetchOrders(); // Refresh to show updated status in real time
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'received': return 'status-received';
      case 'preparing': return 'status-preparing';
      case 'ready': return 'status-ready';
      case 'completed': return 'status-completed';
      default: return 'status-received';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600';
      case 'unpaid': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const generateReceipt = async (orderId) => {
    try {
      // Mark order as paid when generating receipt
      await apiClient.put(`/restaurant/orders/${orderId}/status`, {
        payment_status: 'paid'
      });

      const response = await apiClient.post(`/restaurant/orders/${orderId}/receipt`);
      
      // Create a printable receipt window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt - Order #${orderId.slice(-8)}</title>
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                line-height: 1.4;
              }
              .receipt {
                max-width: 300px;
                margin: 0 auto;
              }
              pre {
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            </style>
          </head>
          <body>
            <div class="receipt">
              <pre>${response.data.receipt_content}</pre>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      
      toast.success('Receipt generated and order marked as paid!');
      fetchOrders(); // Refresh to show updated payment status in real time
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    }
  };

  // Cleanup function removed - automatic cleanup now handled by backend

  const printOrder = (order) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Order #${order.id.slice(-8)}</title>
          <style>
            body { 
              margin: 0; 
              padding: 10px; 
              font-family: 'Courier New', monospace; 
              font-size: 11px; 
              line-height: 1.3;
              max-width: 280px;
              background: white;
            }
            .header { 
              text-align: center; 
              margin-bottom: 15px; 
              border-bottom: 2px solid black; 
              padding-bottom: 8px; 
            }
            .table-number {
              font-size: 20px; 
              font-weight: bold; 
              margin: 8px 0; 
              border: 2px solid black; 
              padding: 8px; 
              display: inline-block;
              background: black;
              color: white;
            }
            .order-info {
              margin: 3px 0;
              font-size: 10px;
            }
            .customer-info {
              margin: 8px 0;
              padding: 5px 0;
              border-top: 1px solid black;
              border-bottom: 1px solid black;
            }
            .items-section { 
              margin: 10px 0; 
            }
            .items-header {
              font-weight: bold;
              margin-bottom: 5px;
              text-decoration: underline;
            }
            .item { 
              display: flex; 
              justify-content: space-between; 
              margin: 2px 0;
              padding: 1px 0;
            }
            .item-name { 
              flex: 1; 
              margin-right: 5px;
            }
            .item-price { 
              text-align: right; 
              min-width: 50px;
              font-weight: bold;
            }
            .item-note {
              font-size: 9px; 
              color: #333; 
              margin: 2px 0 2px 10px; 
              font-style: italic;
              background: #f0f0f0;
              padding: 2px 4px;
            }
            .total-section { 
              margin-top: 15px; 
              border-top: 2px solid black; 
              padding-top: 8px;
              text-align: center;
            }
            .total { 
              font-weight: bold; 
              font-size: 14px;
              margin: 5px 0;
            }
            .special-notes {
              margin-top: 10px;
              border-top: 1px solid black;
              padding-top: 8px;
            }
            .footer { 
              margin-top: 15px; 
              text-align: center; 
              border-top: 2px solid black; 
              padding-top: 8px;
              font-size: 10px;
            }
            @media print { 
              body { 
                margin: 0; 
                padding: 5px; 
              }
              @page { 
                size: 80mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2 style="margin: 0 0 5px 0;">KITCHEN ORDER</h2>
            <div class="table-number">
              TABLE ${order.table_info?.table_number || 'N/A'}
            </div>
            <div class="order-info">Order #${order.id.slice(-8)}</div>
            <div class="order-info">${formatLocalDateTime(order.created_at)}</div>
          </div>

          <div class="customer-info">
            <div><strong>Customer:</strong> Walk-in</div>
            <div><strong>Location:</strong> ${order.table_info?.qr_source || 'Table ' + (order.table_info?.table_number || 'Unknown')}</div>
            <div><strong>Status:</strong> ${order.order_status.toUpperCase()}</div>
            <div><strong>Payment:</strong> ${order.payment_method} (${order.payment_status})</div>
          </div>
          
          <div class="items-section">
            <div class="items-header">ITEMS TO PREPARE:</div>
            ${order.items.map(item => `
              <div class="item">
                <span class="item-name">${item.quantity}x ${item.menu_item_name}</span>
                <span class="item-price">{formatPrice(item.subtotal, currency)}</span>
              </div>
              ${item.notes ? `<div class="item-note" style="font-size: 10px; color: #666; margin-left: 10px; margin-bottom: 5px;">★ ${item.notes}</div>` : ''}
            `).join('')}
          </div>
          
          <div class="total-section">
            ${order.tax_breakdown && order.tax_breakdown.length > 0 ? `
              <div style="margin-bottom: 8px; font-size: 11px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                  <span>Subtotal:</span>
                  <span>{formatPrice(order.subtotal || 0, currency)}</span>
                </div>
                ${order.tax_breakdown.map(tax => `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 2px; ${tax.amount < 0 ? 'color: green;' : ''}">
                    <span>${tax.name} (${tax.type === 'percentage' ? `${tax.rate}%` : 'Fixed'}):</span>
                    <span>{tax.amount < 0 ? '-' : ''}{formatPrice(Math.abs(tax.amount), currency)}</span>
                  </div>
                `).join('')}
                <div style="display: flex; justify-content: space-between; margin-bottom: 2px; font-weight: bold; border-top: 1px solid #000; padding-top: 3px;">
                  <span>Total:</span>
                  <span>{formatPrice(order.total_amount || 0, currency)}</span>
                </div>
              </div>
            ` : ''}
            <div class="total">ORDER TOTAL: {formatPrice(order.total_amount, currency)}</div>
          </div>
          
          ${order.notes ? `
            <div class="special-notes" style="margin-top: 10px; border-top: 2px solid #000; padding-top: 8px;">
              <div style="font-weight: bold; margin-bottom: 5px; text-align: center;">⚠️ SPECIAL INSTRUCTIONS ⚠️</div>
              <div style="background: #f0f0f0; padding: 8px; border: 2px solid #333; border-radius: 4px; font-weight: bold;">${order.notes}</div>
            </div>
          ` : ''}
          
          <div class="footer">
            <div>Kitchen Copy</div>
            <div>${new Date().toLocaleString()}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const printReceipt = async (orderId) => {
    setLoading(true);
    try {
      // Mark order as paid when printing receipt
      await apiClient.put(`/restaurant/orders/${orderId}/status`, {
        payment_status: 'paid'
      });

      // Get the order details for the receipt
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // Helper function to format price in HTML context
      const formatReceiptPrice = (price, curr) => {
        const amount = parseFloat(price || 0);
        const currencyInfo = {
          'USD': '$', 'EUR': '€', 'GBP': '£', 'CAD': 'C$', 'AUD': 'A$', 
          'JPY': '¥', 'CNY': '¥', 'INR': '₹', 'BRL': 'R$', 'MXN': '$',
          'ZAR': 'R', 'SGD': 'S$', 'HKD': 'HK$', 'NZD': 'NZ$', 'SEK': 'kr',
          'NOK': 'kr', 'DKK': 'kr', 'CHF': 'CHF', 'RUB': '₽', 'KRW': '₩',
          'THB': '฿', 'MYR': 'RM', 'PHP': '₱', 'VND': '₫', 'AED': 'AED',
          'SAR': 'SAR', 'TRY': '₺', 'ILS': '₪', 'PKR': '₨', 'BDT': '৳'
        };
        
        const symbol = currencyInfo[curr] || '$';
        const noDecimalCurrencies = ['JPY', 'KRW', 'VND'];
        
        if (noDecimalCurrencies.includes(curr)) {
          return `${symbol}${Math.round(amount).toLocaleString()}`;
        }
        
        return `${symbol}${amount.toFixed(2)}`;
      };

      // Use configured receipt width (default to 80mm if not set)
      const receiptWidth = parseInt(receiptConfig.paper_width) || 80;
      const fontSize = receiptWidth === 58 ? '10px' : '12px';

      // Generate items HTML
      const itemsHtml = order.items.map(item => `
        <div class="item">
          <div class="item-details">
            <div>${item.quantity}x ${item.menu_item_name}</div>
          </div>
          <div class="item-price">${formatReceiptPrice(item.subtotal, currency)}</div>
        </div>
      `).join('');

      // Generate tax breakdown HTML
      let taxBreakdownHtml = '';
      if (order.tax_breakdown && order.tax_breakdown.length > 0 && receiptConfig.show_tax_breakdown !== false) {
        taxBreakdownHtml = `
          <div style="margin-bottom: 10px; font-size: 11px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span>Subtotal:</span>
              <span>${formatReceiptPrice(order.subtotal || 0, currency)}</span>
            </div>
            ${order.tax_breakdown.map(tax => `
              <div style="display: flex; justify-content: space-between; margin-bottom: 3px; ${tax.amount < 0 ? 'color: green;' : ''}">
                <span>${tax.name} (${tax.type === 'percentage' ? `${tax.rate}%` : 'Fixed'}):</span>
                <span>${tax.amount < 0 ? '-' : ''}${formatReceiptPrice(Math.abs(tax.amount), currency)}</span>
              </div>
            `).join('')}
            <div style="display: flex; justify-content: space-between; margin-bottom: 3px; font-weight: bold; border-top: 1px solid #000; padding-top: 5px;">
              <span>Total:</span>
              <span>${formatReceiptPrice(order.total_amount || 0, currency)}</span>
            </div>
          </div>
        `;
      }

      // Create receipt HTML with saved configuration
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Receipt - Order #${order.id.slice(-8)}</title>
          <style>
            body { 
              font-family: 'Courier New', monospace; 
              margin: 0; 
              padding: 10px; 
              font-size: ${fontSize};
              line-height: 1.4;
              width: 100%;
              max-width: ${receiptWidth}mm;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid black; 
              padding-bottom: 10px; 
              margin-bottom: 15px;
            }
            .business-info {
              text-align: center;
              margin-bottom: 15px;
              border-bottom: 1px solid black;
              padding-bottom: 10px;
            }
            .order-info { 
              margin-bottom: 15px; 
              font-size: 11px;
            }
            .items { 
              margin-bottom: 15px; 
            }
            .item { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 5px;
              align-items: flex-start;
            }
            .item-details {
              flex: 1;
              margin-right: 10px;
            }
            .item-price {
              font-weight: bold;
              white-space: nowrap;
            }
            .total-section { 
              border-top: 2px solid black; 
              padding-top: 10px; 
              margin-top: 15px;
            }
            .total { 
              font-weight: bold; 
              font-size: 14px;
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
            }
            .footer { 
              text-align: center; 
              margin-top: 20px; 
              border-top: 1px solid black; 
              padding-top: 10px;
              font-size: 10px;
            }
            @media print { 
              body { 
                margin: 0; 
                padding: 5px; 
              }
              @page { 
                size: ${receiptWidth}mm auto; 
                margin: 0; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2 style="margin: 0;">${(receiptConfig.business_name || 'RECEIPT').toUpperCase()}</h2>
            ${receiptConfig.additional_header_text ? `<div>${receiptConfig.additional_header_text}</div>` : ''}
          </div>

          <div class="business-info">
            ${receiptConfig.business_address ? `<div>${receiptConfig.business_address}</div>` : ''}
            ${receiptConfig.business_phone ? `<div>Phone: ${receiptConfig.business_phone}</div>` : ''}
            ${receiptConfig.business_email ? `<div>Email: ${receiptConfig.business_email}</div>` : ''}
            ${receiptConfig.tax_id ? `<div>Tax ID: ${receiptConfig.tax_id}</div>` : ''}
          </div>

          <div class="order-info">
            <div><strong>Order #${order.id.slice(-8)}</strong></div>
            <div>Date: ${formatLocalDateTime(order.created_at)}</div>
            <div>Table: ${order.table_info?.table_number || 'N/A'}</div>
            <div><strong>Status: PAID</strong></div>
          </div>
          
          <div class="items">
            <div style="border-bottom: 1px solid black; padding-bottom: 5px; margin-bottom: 10px; font-weight: bold;">ITEMS:</div>
            ${itemsHtml}
          </div>
          
          <div class="total-section">
            ${taxBreakdownHtml}
            <div class="total">
              <span>TOTAL:</span>
              <span>${formatReceiptPrice(order.total_amount, currency)}</span>
            </div>
            <p style="margin: 10px 0 5px 0; font-size: 11px;"><strong>Status: PAID</strong></p>
          </div>
          
          <div class="footer">
            <div>${receiptConfig.footer_text || 'Thank you for your business!'}</div>
            <div>${new Date().toLocaleString()}</div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      
      toast.success('Receipt printed and order marked as paid!');
      fetchOrders(); // Refresh to show updated payment status in real time
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt');
    } finally {
      setLoading(false);
    }
  };

  // Payment Management Functions
  // Payment functions removed as requested

  const combineOrders = async () => {
    if (combineModal.selectedOrders.length < 2) {
      toast.error('Please select at least 2 orders to combine');
      return;
    }

    // Validate all selected orders are from the same table
    const selectedOrdersData = orders.filter(order => combineModal.selectedOrders.includes(order.id));
    const tableNumbers = [...new Set(selectedOrdersData.map(order => order.table_info?.table_number).filter(Boolean))];
    
    if (tableNumbers.length === 0) {
      toast.error('Selected orders must have valid table information');
      return;
    }
    
    if (tableNumbers.length > 1) {
      toast.error(`Cannot combine orders from different tables. Found orders from tables: ${tableNumbers.join(', ')}`);
      return;
    }

    // Calculate totals for confirmation
    const totalAmount = selectedOrdersData.reduce((sum, order) => sum + (order.total_amount || 0), 0);
    const totalItems = selectedOrdersData.reduce((sum, order) => sum + (order.items?.length || 0), 0);

    // Show custom confirmation modal
    setCombineConfirmData({
      orderCount: selectedOrdersData.length,
      tableNumber: tableNumbers[0],
      totalItems,
      totalAmount
    });
    setShowCombineConfirm(true);
  };

  const executeCombineOrders = async () => {
    setShowCombineConfirm(false);

    const mainOrderId = combineModal.selectedOrders[0];
    const ordersToCombine = combineModal.selectedOrders.slice(1);

    setLoading(true);
    try {
      const response = await apiClient.post('/restaurant/orders/combine', {
        main_order_id: mainOrderId,
        orders_to_combine: ordersToCombine
      });
      
      toast.success(
        `Orders combined successfully! New order created with status: Completed and Payment: Paid`,
        { duration: 5000 }
      );
      setCombineModal({ show: false, selectedOrders: [] });
      fetchOrders();
    } catch (error) {
      console.error('Error combining orders:', error);
      toast.error(error.response?.data?.detail || 'Failed to combine orders');
    } finally {
      setLoading(false);
    }
  };

  // Payment modal function removed as requested

  const toggleOrderForCombining = (orderId) => {
    setCombineModal(prev => ({
      ...prev,
      selectedOrders: prev.selectedOrders.includes(orderId)
        ? prev.selectedOrders.filter(id => id !== orderId)
        : [...prev.selectedOrders, orderId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block">
              <div className="spinner w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-3">Loading orders...</p>
          </div>
        </div>
      )}

      {/* Show content only when not loading */}
      {!loading && (
        <>
          {/* Zero Capacity Alert Banner */}
          {restaurant?.order_capacity === 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="text-red-800 font-bold text-lg">No Order Capacity Available!</h3>
                <p className="text-red-700 text-sm mt-1">
                  Your order capacity is <strong>0</strong>. Customers cannot place new orders. Please purchase more capacity to continue receiving orders.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (onPurchaseCapacity) {
                  onPurchaseCapacity();
                }
              }}
              className="btn-primary whitespace-nowrap px-6 py-3 text-base font-semibold"
            >
              💳 Purchase Capacity
            </button>
          </div>
        </div>
      )}

      {/* Low Capacity Warning Banner */}
      {restaurant?.order_capacity > 0 && restaurant?.order_capacity <= 50 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="text-yellow-800 font-bold text-lg">Order Capacity is Low!</h3>
                <p className="text-yellow-700 text-sm mt-1">
                  You have <strong>{restaurant?.order_capacity} orders remaining</strong>. Please purchase more capacity to ensure uninterrupted service.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (onPurchaseCapacity) {
                  onPurchaseCapacity();
                }
              }}
              className="btn-primary whitespace-nowrap px-6 py-3 text-base font-semibold"
            >
              💳 Purchase Capacity
            </button>
          </div>
        </div>
      )}
      
      {/* Combine Orders Confirmation Modal */}
      {showCombineConfirm && combineConfirmData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-3xl">🔗</span>
              <h3 className="text-xl font-bold text-gray-900">Combine Orders</h3>
            </div>
            
            <p className="text-gray-700 mb-4">
              Combine <strong>{combineConfirmData.orderCount} orders</strong> from <strong>Table {combineConfirmData.tableNumber}</strong>?
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-800 mb-2">📋 Order Summary:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Total Items: <strong>{combineConfirmData.totalItems}</strong></li>
                <li>• Total Amount: <strong>{formatPrice(combineConfirmData.totalAmount, currency)}</strong></li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Note:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Creates a single <strong>completed</strong> order</li>
                <li>• Payment status set to <strong>paid</strong></li>
                <li>• Original orders will be <strong>deleted</strong></li>
                <li>• This action cannot be undone</li>
              </ul>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={executeCombineOrders}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                Confirm & Combine
              </button>
              <button
                onClick={() => setShowCombineConfirm(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="card">
        <div className="card-header">
          {/* Header Title - Always visible */}
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Order Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              📅 Only orders from the last 24 hours are displayed here. Older orders are automatically archived to daily reports.
            </p>
          </div>

          {/* Mobile-Responsive Controls */}
          <div className="space-y-4 md:space-y-0">
            {/* First Row: Filters */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                  Filter by Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  data-testid="order-status-filter"
                  className="form-input w-full"
                >
                  {orderStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                  Filter by Payment
                </label>
                <select
                  value={selectedPaymentStatus}
                  onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                  data-testid="payment-status-filter"
                  className="form-input w-full"
                >
                  {paymentStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                  Filter by Table
                </label>
                <select
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  data-testid="table-filter"
                  className="form-input w-full"
                >
                  <option value="">All Tables</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.table_number}>
                      Table {table.table_number}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Second Row: Action Buttons and Auto-Refresh Controls */}
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <button
                onClick={fetchOrders}
                data-testid="refresh-orders-button"
                className="btn-secondary flex items-center justify-center gap-2 flex-1 sm:flex-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="md:inline">Refresh</span>
              </button>
              
              {/* Auto-Refresh Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border">
                <input
                  type="checkbox"
                  id="auto-refresh-toggle"
                  checked={autoRefreshEnabled}
                  onChange={toggleAutoRefresh}
                  className="rounded border-gray-300"
                />
                <label htmlFor="auto-refresh-toggle" className="text-sm text-gray-700 cursor-pointer">
                  Auto-refresh (2 min)
                </label>
              </div>
              
              {/* Last Updated Display */}
              {lastUpdated && (
                <div className="text-xs text-gray-500 px-2 py-1">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              
              <button
                onClick={() => setCombineModal({ show: true, selectedOrders: [] })}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center justify-center gap-2 flex-1 sm:flex-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
                <span>Combine Orders</span>
              </button>
            </div>
          </div>
          
          {/* Combine Orders Mode */}
          {combineModal.show && (() => {
            const selectedOrdersData = orders.filter(order => combineModal.selectedOrders.includes(order.id));
            const tableNumbers = [...new Set(selectedOrdersData.map(order => order.table_info?.table_number).filter(Boolean))];
            const totalAmount = selectedOrdersData.reduce((sum, order) => sum + (order.total_amount || 0), 0);
            const totalItems = selectedOrdersData.reduce((sum, order) => sum + (order.items?.length || 0), 0);
            const isValidSelection = combineModal.selectedOrders.length >= 2 && tableNumbers.length === 1;
            
            return (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-purple-900 mb-2">Order Combination Mode</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-purple-800">Selected Orders:</span>
                        <div className="text-purple-700">{combineModal.selectedOrders.length} orders</div>
                      </div>
                      <div>
                        <span className="font-medium text-purple-800">Table Validation:</span>
                        <div className={`${tableNumbers.length === 1 ? 'text-green-700' : tableNumbers.length > 1 ? 'text-red-700' : 'text-gray-600'}`}>
                          {tableNumbers.length === 0 ? 'No tables selected' : 
                           tableNumbers.length === 1 ? `Table ${tableNumbers[0]} ✓` :
                           `Multiple tables (${tableNumbers.join(', ')}) ✗`}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-purple-800">Combined Total:</span>
                        <div className="text-purple-700">
                          {totalItems} items • {formatPrice(totalAmount, currency)}
                        </div>
                      </div>
                    </div>
                    {combineModal.selectedOrders.length >= 2 && tableNumbers.length > 1 && (
                      <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                        ⚠️ All selected orders must be from the same table to combine
                      </div>
                    )}
                    {isValidSelection && (
                      <div className="mt-2 p-2 bg-green-100 border border-green-200 rounded text-sm text-green-700">
                        ✓ Ready to combine - This will create a single completed order and delete the originals
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    <button
                      onClick={combineOrders}
                      disabled={!isValidSelection}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Combine Selected
                    </button>
                    <button
                      onClick={() => setCombineModal({ show: false, selectedOrders: [] })}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="no-orders-message">
              {selectedStatus ? `No ${selectedStatus} orders found.` : 'No orders received yet.'}
            </div>
          ) : (
            orders.map((order) => (
              <div
                key={order.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                data-testid={`order-${order.id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-gray-900" data-testid={`order-id-${order.id}`}>
                        Order #{order.id?.slice(-8) || 'N/A'}
                      </h3>
                      <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                        Table {order.table_info?.table_number || 'Unknown'}
                      </span>
                    </div>
                    {order.customer_name && (
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        👤 {order.customer_name}
                      </p>
                    )}
                    <p className="text-sm text-gray-500">
                      {formatLocalDateTime(order.created_at)}
                    </p>
                    {order.table_info?.qr_source && (
                      <p className="text-xs text-gray-400">
                        {order.table_info.qr_source}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={getStatusBadgeClass(order.order_status)} data-testid={`order-status-${order.id}`}>
                      {order.order_status}
                    </span>
                    {order.receipt_generated && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        Receipt Generated
                      </span>
                    )}
                    {/* Print and Receipt buttons removed as requested */}
                    {combineModal.show && (() => {
                      const isSelected = combineModal.selectedOrders.includes(order.id);
                      const canSelect = !order.is_combined_order && order.order_status !== 'completed';
                      const isDisabledReason = order.is_combined_order ? 'Already combined' : 
                                             order.order_status === 'completed' ? 'Already completed' : null;
                      
                      return (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => canSelect && toggleOrderForCombining(order.id)}
                            disabled={!canSelect}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              isSelected
                                ? 'bg-purple-600 text-white'
                                : canSelect
                                ? 'bg-purple-200 text-purple-800 hover:bg-purple-300'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                            title={isDisabledReason || (isSelected ? 'Click to deselect' : 'Click to select for combining')}
                          >
                            {isSelected ? '✓ Selected' : canSelect ? 'Select' : '✗ Cannot Select'}
                          </button>
                          {isDisabledReason && (
                            <span className="text-xs text-gray-500">{isDisabledReason}</span>
                          )}
                        </div>
                      );
                    })()}
                    {/* Payment button removed as requested */}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    {/* Customer information removed from display */}
                    {order.table_info && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Table Capacity:</span> {order.table_info.capacity} seats
                      </p>
                    )}
                    {order.created_by_restaurant && (
                      <p className="text-xs text-purple-600 font-medium">
                        📞 Restaurant Created Order
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      Total Amount: {formatPrice(order.total_amount || 0, currency)}
                    </p>
                    
                    <div className="space-y-1 mb-2">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Status:</span> 
                        <span className={`ml-1 ${getPaymentStatusColor(order.payment_status)} font-medium`}>
                          {order.payment_status === 'paid' ? 'Paid' : order.payment_status === 'unpaid' ? 'Unpaid' : order.payment_status}
                        </span>
                      </p>
                      
                      {order.payment_status === 'paid' && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Payment:</span> Card
                        </p>
                      )}
                    </div>

                    {/* Bill Components */}
                    {(order.subtotal !== undefined || order.total_amount !== undefined) && (
                      <div className="bg-gray-50 rounded p-2 text-xs space-y-1">
                        <div className="font-medium text-gray-700 border-b pb-1">Bill Breakdown:</div>
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span>{formatPrice(order.subtotal || 0, currency)}</span>
                        </div>
                        
                        {/* Show detailed tax breakdown if available */}
                        {(order.tax_breakdown && order.tax_breakdown.length > 0) ? (
                          order.tax_breakdown.map((tax, index) => (
                            <div key={index} className="flex justify-between">
                              <span>{tax.name} ({tax.type === 'percentage' ? `${tax.rate}%` : 'Fixed'}):</span>
                              <span className={tax.amount < 0 ? 'text-green-600' : ''}>
                                {tax.amount < 0 ? '-' : ''}{formatPrice(Math.abs(tax.amount), currency)}
                              </span>
                            </div>
                          ))
                        ) : (
                          /* Reconstruct tax components for orders with missing tax_breakdown */
                          order.subtotal !== undefined && order.total_amount !== undefined && (() => {
                            // Calculate tax components based on order totals
                            const subtotal = parseFloat(order.subtotal || 0);
                            const totalAmount = parseFloat(order.total_amount || 0);
                            const totalTax = parseFloat(order.total_tax || (totalAmount - subtotal));
                            
                            if (Math.abs(totalTax) < 0.01) return null; // No taxes to show
                            
                            // Standard tax configuration based on common restaurant setup
                            const reconstructedTaxes = [];
                            
                            // If we have a significant difference, try to reconstruct components
                            if (subtotal > 0) {
                              // GST 7% 
                              const gstAmount = Math.round((subtotal * 0.07) * 100) / 100;
                              reconstructedTaxes.push({
                                name: 'GST',
                                type: 'percentage', 
                                rate: 7,
                                amount: gstAmount
                              });
                              
                              // CST 4%
                              const cstAmount = Math.round((subtotal * 0.04) * 100) / 100;
                              reconstructedTaxes.push({
                                name: 'CST', 
                                type: 'percentage',
                                rate: 4,
                                amount: cstAmount
                              });
                              
                              // Calculate remaining amount (could be tips, discounts, etc.)
                              const calculatedTax = gstAmount + cstAmount;
                              const remainingTax = Math.round((totalTax - calculatedTax) * 100) / 100;
                              
                              if (Math.abs(remainingTax) >= 0.01) {
                                reconstructedTaxes.push({
                                  name: remainingTax < 0 ? 'Discount' : 'Service Charge',
                                  type: 'fixed',
                                  rate: null,
                                  amount: remainingTax
                                });
                              }
                            } else {
                              // Fallback: show total tax as single line item
                              reconstructedTaxes.push({
                                name: 'Tax/Fees',
                                type: 'fixed', 
                                rate: null,
                                amount: totalTax
                              });
                            }
                            
                            return reconstructedTaxes.map((tax, index) => (
                              <div key={`reconstructed-${index}`} className="flex justify-between">
                                <span>{tax.name} ({tax.type === 'percentage' && tax.rate ? `${tax.rate}%` : 'Fixed'}):</span>
                                <span className={tax.amount < 0 ? 'text-green-600' : ''}>
                                  {tax.amount < 0 ? '-' : ''}{formatPrice(Math.abs(tax.amount), currency)}
                                </span>
                              </div>
                            ));
                          })()
                        )}
                        
                        <div className="flex justify-between font-medium border-t pt-1">
                          <span>Total:</span>
                          <span>{formatPrice(order.total_amount || 0, currency)}</span>
                        </div>
                      </div>
                    )}
                    
                    {order.is_combined_order && (
                      <p className="text-xs text-blue-600 font-medium mt-2">
                        Combined into Order #{order.parent_order_id?.slice(-8) || 'N/A'}
                      </p>
                    )}
                    
                    {order.combined_from_orders && order.combined_from_orders.length > 0 && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">
                          🔗 Combined Order (from {order.combined_from_orders.length} original orders)
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Original orders: {order.combined_from_orders.map(id => `#${id.slice(-8)}`).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Items:</h4>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex text-sm">
                        <div className="flex-1 min-w-0 pr-3">
                          <span className="font-medium break-words">{item.quantity}x {item.menu_item_name}</span>
                          {item.notes && (
                            <div className="text-xs text-gray-500 italic mt-1">Note: {item.notes}</div>
                          )}
                        </div>
                        <div className="flex-shrink-0 font-medium text-right whitespace-nowrap">
                          {formatPrice(item.subtotal, currency)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {order.notes && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-orange-700">⚠️ Special Instructions:</span> 
                      <span className="ml-2">{order.notes}</span>
                    </p>
                  </div>
                )}

                {/* Status Update Buttons */}
                <div className="flex flex-wrap gap-2">
                  {order.order_status !== 'preparing' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'preparing')}
                      disabled={loading}
                      data-testid={`status-preparing-${order.id}`}
                      className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
                    >
                      Mark Preparing
                    </button>
                  )}
                  {order.order_status !== 'ready' && order.order_status !== 'completed' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'ready')}
                      disabled={loading}
                      data-testid={`status-ready-${order.id}`}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Mark Ready
                    </button>
                  )}
                  {order.order_status !== 'completed' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'completed')}
                      disabled={loading}
                      data-testid={`status-completed-${order.id}`}
                      className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                    >
                      Mark Completed
                    </button>
                  )}
                  
                  {/* Print Receipt Button */}
                  <button
                    onClick={() => printReceipt(order.id)}
                    disabled={loading}
                    data-testid={`print-receipt-${order.id}`}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center"
                  >
                    <span className="mr-1">🧾</span>
                    Print Receipt
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
        </>
      )}

      {/* Payment Modal removed as requested */}
    </div>
  );
};

export default OrderManagement;